import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import Drawer from '@app/components/Drawer';
import LabelPairsEditor from '@app/components/LabelPairsEditor';
import { KeyIcon, SaveIcon } from '@app/components/icons';
import {
  Credential,
  CredentialDetailsApiRestAuth,
  CredentialDetailsApiRestschema,
  CredentialDetailsApiRestAuthApiKey,
  CredentialDetailsApiRestAuthBasic,
  CredentialDetailsApiRestAuthBearer,
  CredentialDetailsApiRestAuthMtls,
  CredentialDetailsCliSshschema,
  CredentialDetailsGnmischema,
  CredentialDetailsSnmpV2cschema,
  CredentialDetailsSnmpV3schema,
  CredentialStore,
  CredentialStoresListResponse,
  CredentialKindschema,
  CredentialUpdate,
  CredentialUpdateDetails,
  Secret,
  SecretTypeschema,
  SecretSetValue,
  SnmpV3AuthProtocolschema,
  SnmpV3PrivProtocolschema,
  SnmpV3SecurityLevelschema
} from '@app/types/sdk';
import { useCredentialStoresApi, useCredentialsApi, useSecretsApi } from '@app/hooks/useApis';
import { useApiQuery } from '@app/hooks/useApiQuery';
import { resolveErrorMessage } from '@app/utils/errors';
import { labelPairsToRecord, mapLabelsToPairs, type LabelPair } from '@app/utils/labels';
import { collectCredentialSecretIds, containsCertificatePemBlock } from '@app/utils/secrets';

interface CredentialDrawerProps {
  orgId: string;
  credentialId: string | null;
  open: boolean;
  onClose: () => void;
  onUpdated: (credential: Credential, etag: string | null) => void;
  onRequestAssignment?: (credential: Credential) => void;
}

interface SecretRecord {
  secret: Secret;
  etag: string | null;
}

interface RotationState {
  value: string;
  saving: boolean;
  error: string | null;
  editing: boolean;
}

type CliDetailsWithAuth = CredentialDetailsCliSshschema & { auth?: string };
type CliUpdateDetailsWithAuth = CredentialUpdateDetails & { auth?: string };
type CliDetailsWithCyberark = CredentialDetailsCliSshschema & { cyberarkStoreId?: string };

const isMtlsPemPair = (
  value: CredentialDetailsApiRestAuthMtls
): value is { clientCertSecretId: string; clientKeySecretId: string; caBundleSecretId?: string } => 'clientCertSecretId' in value;

const isMtlsPkcs12 = (
  value: CredentialDetailsApiRestAuthMtls
): value is { pkcs12SecretId: string; pkcs12PassphraseSecretId?: string } => 'pkcs12SecretId' in value;

const formatFingerprint = (secret?: Secret): string => {
  if (!secret) {
    return '—';
  }
  const fingerprint = secret.fingerprint ? secret.fingerprint.slice(0, 16) : null;
  const last4 = secret.last4 ? `…${secret.last4}` : '';
  return fingerprint ? `${fingerprint}${last4}` : last4 || '—';
};

const formatTimestamp = (date?: Date | string): string => {
  if (!date) {
    return '—';
  }
  const instance = date instanceof Date ? date : new Date(date);
  return instance.toLocaleString();
};

const resolveSecretStatus = (credential: Credential): string => {
  switch (credential.kind) {
    case CredentialKindschema.CliSsh: {
      const details = credential.details as CliDetailsWithCyberark;
      if (details.cyberarkStoreId) {
        return 'OK';
      }
      return details.execPasswordSecretId || details.sshPrivateKeySecretId ? 'OK' : 'Missing';
    }
    case CredentialKindschema.Gnmi: {
      const details = credential.details as CredentialDetailsGnmischema;
      return details.passwordSecretId && details.deviceServerTrustCaBundleSecretId ? 'OK' : 'Missing';
    }
    case CredentialKindschema.SnmpV2c: {
      const details = credential.details as CredentialDetailsSnmpV2cschema;
      return details.readCommunitySecretId ? 'OK' : 'Missing';
    }
    case CredentialKindschema.SnmpV3: {
      const details = credential.details as CredentialDetailsSnmpV3schema;
      if (details.securityLevel === SnmpV3SecurityLevelschema.NoAuthNoPriv) {
        return 'OK';
      }
      if (details.securityLevel === SnmpV3SecurityLevelschema.AuthNoPriv) {
        return details.authPasswordSecretId ? 'OK' : 'Missing';
      }
      return details.authPasswordSecretId && details.privPasswordSecretId ? 'OK' : 'Missing';
    }
    case CredentialKindschema.ApiRest: {
      const details = credential.details as CredentialDetailsApiRestschema;
      const auth = details.auth;
      if (!auth) {
        return '—';
      }
      if (auth.type === 'api_key') {
        return auth.apiKey?.apiKeySecretId ? 'OK' : 'Missing';
      }
      if (auth.type === 'basic') {
        return auth.basic?.passwordSecretId ? 'OK' : 'Missing';
      }
      if (auth.type === 'bearer') {
        return auth.bearer?.tokenSecretId ? 'OK' : 'Missing';
      }
      if (auth.type === 'mtls' && auth.mtls) {
        const mtls = auth.mtls as CredentialDetailsApiRestAuthMtls;
        if (isMtlsPemPair(mtls)) {
          return mtls.clientCertSecretId && mtls.clientKeySecretId ? 'OK' : 'Missing';
        }
        if (isMtlsPkcs12(mtls)) {
          return mtls.pkcs12SecretId ? 'OK' : 'Missing';
        }
        return 'Missing';
      }
      return '—';
    }
    default:
      return '—';
  }
};

const CredentialDrawer = ({ orgId, credentialId, open, onClose, onUpdated, onRequestAssignment }: CredentialDrawerProps) => {
  const credentialsApi = useCredentialsApi();
  const secretsApi = useSecretsApi();
  const credentialStoresApi = useCredentialStoresApi();

  const credentialStoresFetcher = useMemo(() => {
    if (!orgId || !open) {
      return null;
    }
    return () => credentialStoresApi.orgsOrgIdCredentialStoresGet({ orgId, limit: 200 });
  }, [credentialStoresApi, open, orgId]);

  const {
    data: credentialStoresList,
    loading: credentialStoresLoading,
    error: credentialStoresError
  } = useApiQuery<CredentialStoresListResponse>(credentialStoresFetcher, [credentialStoresFetcher]);

  const credentialStores: CredentialStore[] = credentialStoresList?.items ?? [];

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [credential, setCredential] = useState<Credential | null>(null);
  const [etag, setEtag] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [labelPairs, setLabelPairs] = useState<LabelPair[]>([]);
  const [labelsError, setLabelsError] = useState<string | null>(null);

  const [cliDetails, setCliDetails] = useState<CliDetailsWithCyberark | null>(null);
  const [gnmiDetails, setGnmiDetails] = useState<CredentialDetailsGnmischema | null>(null);
  const [snmpV2cDetails, setSnmpV2cDetails] = useState<CredentialDetailsSnmpV2cschema | null>(null);
  const [snmpV3Details, setSnmpV3Details] = useState<CredentialDetailsSnmpV3schema | null>(null);
  const [apiDetails, setApiDetails] = useState<CredentialDetailsApiRestschema | null>(null);

  const [secretRecords, setSecretRecords] = useState<Record<string, SecretRecord>>({});
  const [secretLoading, setSecretLoading] = useState(false);
  const [rotationState, setRotationState] = useState<Record<string, RotationState>>({});

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const secretStatus = credential ? resolveSecretStatus(credential) : '—';

  const resetState = useCallback(() => {
    setCredential(null);
    setEtag(null);
    setName('');
    setDescription('');
    setLabelPairs([]);
    setLabelsError(null);
    setCliDetails(null);
    setGnmiDetails(null);
    setSnmpV2cDetails(null);
    setSnmpV3Details(null);
    setApiDetails(null);
    setSecretRecords({});
    setRotationState({});
    setSaveError(null);
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }
    if (!credentialId) {
      resetState();
      return;
    }
    let isActive = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await credentialsApi.orgsOrgIdCredentialsCredentialIdGetRaw({
          orgId,
          credentialId
        });
        if (!isActive) {
          return;
        }
        const data = await response.value();
        const responseEtag = response.raw.headers.get('ETag');
        setCredential(data);
        setEtag(responseEtag);
        setName(data.name ?? '');
        setDescription(data.description ?? '');
        setLabelPairs(mapLabelsToPairs(data.labels));
        primeDetailState(data);
        await loadSecrets(data);
      } catch (caught) {
        console.error('Failed to load credential', caught);
        if (isActive) {
          setError(caught instanceof Error ? caught.message : 'Failed to load credential details.');
        }
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    const primeDetailState = (record: Credential) => {
      if (record.kind === CredentialKindschema.CliSsh) {
        setCliDetails(record.details as CredentialDetailsCliSshschema);
      } else {
        setCliDetails(null);
      }
      if (record.kind === CredentialKindschema.Gnmi) {
        setGnmiDetails(record.details as CredentialDetailsGnmischema);
      } else {
        setGnmiDetails(null);
      }
      if (record.kind === CredentialKindschema.SnmpV2c) {
        setSnmpV2cDetails(record.details as CredentialDetailsSnmpV2cschema);
      } else {
        setSnmpV2cDetails(null);
      }
      if (record.kind === CredentialKindschema.SnmpV3) {
        setSnmpV3Details(record.details as CredentialDetailsSnmpV3schema);
      } else {
        setSnmpV3Details(null);
      }
      if (record.kind === CredentialKindschema.ApiRest) {
        setApiDetails(record.details as CredentialDetailsApiRestschema);
      } else {
        setApiDetails(null);
      }
    };

    const loadSecrets = async (record: Credential) => {
      const secretIds = Array.from(new Set(collectCredentialSecretIds(record)));
      if (secretIds.length === 0) {
        setSecretRecords({});
        return;
      }
      setSecretLoading(true);
      try {
        const entries: Array<[string, SecretRecord]> = [];
        for (const secretId of secretIds) {
          const response = await secretsApi.orgsOrgIdSecretsSecretIdGetRaw({
            orgId,
            secretId
          });
          if (!isActive) {
            return;
          }
          const secret = await response.value();
          const etag = response.raw.headers.get('ETag');
          entries.push([secretId, { secret, etag }]);
        }
        if (isActive) {
          setSecretRecords(Object.fromEntries(entries));
          setRotationState(
            entries.reduce<Record<string, RotationState>>((acc, [id]) => {
              acc[id] = { value: '', saving: false, error: null, editing: false };
              return acc;
            }, {})
          );
        }
      } catch (caught) {
        console.error('Failed to load secrets', caught);
        if (isActive) {
          setSecretRecords({});
        }
      } finally {
        if (isActive) {
          setSecretLoading(false);
        }
      }
    };

    load();
    return () => {
      isActive = false;
    };
  }, [credentialsApi, credentialId, orgId, open, resetState, secretsApi]);

  const secretList = useMemo(() => Object.entries(secretRecords), [secretRecords]);

  const secretLabelMap = useMemo(() => {
    const map: Record<string, string> = {};
    if (!credential) {
      return map;
    }
    switch (credential.kind) {
      case CredentialKindschema.CliSsh:
        if (cliDetails?.execPasswordSecretId) {
          map[cliDetails.execPasswordSecretId] = 'CLI password';
        }
        if (cliDetails?.enablePasswordSecretId) {
          map[cliDetails.enablePasswordSecretId] = 'Enable password';
        }
        if (cliDetails?.sshPrivateKeySecretId) {
          map[cliDetails.sshPrivateKeySecretId] = 'SSH private key';
        }
        if (cliDetails?.sshPassphraseSecretId) {
          map[cliDetails.sshPassphraseSecretId] = 'SSH key passphrase';
        }
        break;
      case CredentialKindschema.Gnmi:
        if (gnmiDetails?.passwordSecretId) {
          map[gnmiDetails.passwordSecretId] = 'gNMI password';
        }
        if (gnmiDetails?.deviceServerTrustCaBundleSecretId) {
          map[gnmiDetails.deviceServerTrustCaBundleSecretId] = 'Device CA bundle';
        }
        break;
      case CredentialKindschema.SnmpV2c:
        if (snmpV2cDetails?.readCommunitySecretId) {
          map[snmpV2cDetails.readCommunitySecretId] = 'Read community';
        }
        if (snmpV2cDetails?.writeCommunitySecretId) {
          map[snmpV2cDetails.writeCommunitySecretId] = 'Write community';
        }
        break;
      case CredentialKindschema.SnmpV3:
        if (snmpV3Details?.authPasswordSecretId) {
          map[snmpV3Details.authPasswordSecretId] = 'Auth password';
        }
        if (snmpV3Details?.privPasswordSecretId) {
          map[snmpV3Details.privPasswordSecretId] = 'Privacy password';
        }
        break;
      case CredentialKindschema.ApiRest: {
        const auth = apiDetails?.auth;
        if (auth?.type === 'api_key' && auth.apiKey?.apiKeySecretId) {
          map[auth.apiKey.apiKeySecretId] = 'API key';
        }
        if (auth?.type === 'basic' && auth.basic?.passwordSecretId) {
          map[auth.basic.passwordSecretId] = 'Password';
        }
        if (auth?.type === 'bearer' && auth.bearer?.tokenSecretId) {
          map[auth.bearer.tokenSecretId] = 'Bearer token';
        }
        if (auth?.type === 'mtls' && auth.mtls) {
          const mtls = auth.mtls as CredentialDetailsApiRestAuthMtls;
          if (isMtlsPemPair(mtls)) {
            if (mtls.clientCertSecretId) {
              map[mtls.clientCertSecretId] = 'Client certificate';
            }
            if (mtls.clientKeySecretId) {
              map[mtls.clientKeySecretId] = 'Client key';
            }
            if (mtls.caBundleSecretId) {
              map[mtls.caBundleSecretId] = 'CA bundle';
            }
          } else if (isMtlsPkcs12(mtls)) {
            if (mtls.pkcs12SecretId) {
              map[mtls.pkcs12SecretId] = 'PKCS#12';
            }
            if (mtls.pkcs12PassphraseSecretId) {
              map[mtls.pkcs12PassphraseSecretId] = 'PKCS#12 passphrase';
            }
          }
        }
        break;
      }
      default:
        break;
    }
    return map;
  }, [apiDetails, cliDetails, credential, gnmiDetails, snmpV2cDetails, snmpV3Details]);

  const handleRotate = async (secretId: string) => {
    const state = rotationState[secretId];
    if (!state || !credential) {
      return;
    }
    if (!state.value.trim()) {
      setRotationState((prev) => ({
        ...prev,
        [secretId]: { ...state, error: 'Secret value is required.' }
      }));
      return;
    }
    if (
      secretRecords[secretId]?.secret.type === SecretTypeschema.TlsCaBundlePem &&
      !containsCertificatePemBlock(state.value)
    ) {
      setRotationState((prev) => ({
        ...prev,
        [secretId]: { ...state, error: 'Enter a complete PEM certificate bundle.' }
      }));
      return;
    }
    setRotationState((prev) => ({
      ...prev,
      [secretId]: { ...state, saving: true, error: null }
    }));
    try {
      let currentEtag = secretRecords[secretId]?.etag ?? null;
      if (!currentEtag) {
        const current = await secretsApi.orgsOrgIdSecretsSecretIdGetRaw({ orgId, secretId });
        currentEtag = current.raw.headers.get('ETag');
      }
      await secretsApi.orgsOrgIdSecretsSecretIdValuePostRaw({
        orgId,
        secretId,
        ifMatch: currentEtag ?? undefined,
        secretSetValue: { value: state.value } as SecretSetValue
      });
      const refreshed = await secretsApi.orgsOrgIdSecretsSecretIdGetRaw({ orgId, secretId });
      const secret = await refreshed.value();
      const etag = refreshed.raw.headers.get('ETag');
      setSecretRecords((prev) => ({ ...prev, [secretId]: { secret, etag } }));

      try {
        const touchResult = await touchCredentialAfterRotation(credential);
        setCredential(touchResult.credential);
        setEtag(touchResult.etag);
        onUpdated(touchResult.credential, touchResult.etag);
        setSaveError(null);
        setRotationState((prev) => ({
          ...prev,
          [secretId]: { value: '', error: null, saving: false, editing: false }
        }));
      } catch (touchError) {
        console.error('Secret rotated but credential refresh failed', touchError);
        setRotationState((prev) => ({
          ...prev,
          [secretId]: { value: '', error: null, saving: false, editing: false }
        }));
        setSaveError(
          touchError instanceof Error
            ? `Secret rotated, but connector refresh trigger failed: ${touchError.message}`
            : 'Secret rotated, but connector refresh trigger failed.'
        );
      }
    } catch (caught) {
      console.error('Failed to rotate secret', caught);
      const message = await resolveErrorMessage(caught, 'Failed to rotate secret.');
      setRotationState((prev) => ({
        ...prev,
        [secretId]: {
          ...state,
          saving: false,
          error: message
        }
      }));
    }
  };

  const touchCredentialAfterRotation = async (
    record: Credential
  ): Promise<{ credential: Credential; etag: string | null }> => {
    if (!record.id) {
      throw new Error('Credential id is missing.');
    }

    let currentCredentialEtag = etag;
    if (!currentCredentialEtag) {
      const current = await credentialsApi.orgsOrgIdCredentialsCredentialIdGetRaw({
        orgId,
        credentialId: record.id
      });
      currentCredentialEtag = current.raw.headers.get('ETag');
    }

    const response = await credentialsApi.orgsOrgIdCredentialsCredentialIdPatchRaw({
      orgId,
      credentialId: record.id,
      ifMatch: currentCredentialEtag ?? undefined,
      credentialUpdate: {
        name: record.name ?? undefined,
        description: record.description ?? undefined,
        labels: record.labels && Object.keys(record.labels).length > 0 ? record.labels : undefined,
        details: buildUpdateDetailsForRecord(record)
      }
    });
    const updated = await response.value();
    return { credential: updated, etag: response.raw.headers.get('ETag') };
  };

  const handleSave = async () => {
    if (!credential || !credentialId) {
      return;
    }
    const labelsResult = labelPairsToRecord(labelPairs);
    if (labelsResult.error) {
      setLabelsError(labelsResult.error);
      return;
    }
    setLabelsError(null);
    setSaving(true);
    setSaveError(null);
    try {
      const payload: CredentialUpdate = {
        name: name.trim() || undefined,
        description: description.trim() || undefined,
        labels: Object.keys(labelsResult.labels).length > 0 ? labelsResult.labels : undefined,
        details: buildUpdateDetails(credential.kind)
      };
      const response = await credentialsApi.orgsOrgIdCredentialsCredentialIdPatchRaw({
        orgId,
        credentialId,
        ifMatch: etag ?? undefined,
        credentialUpdate: payload
      });
      const updated = await response.value();
      const updatedEtag = response.raw.headers.get('ETag');
      setCredential(updated);
      setEtag(updatedEtag);
      onUpdated(updated, updatedEtag);
    } catch (caught) {
      console.error('Failed to save credential', caught);
      setSaveError(await resolveErrorMessage(caught, 'Failed to save changes.'));
    } finally {
      setSaving(false);
    }
  };

  const buildUpdateDetails = (kind: Credential['kind']): CredentialUpdateDetails | undefined => {
    if (kind === CredentialKindschema.CliSsh && cliDetails) {
      const details: CliUpdateDetailsWithAuth = {
        username: cliDetails.username,
        port: cliDetails.port,
        execPasswordSecretId: cliDetails.execPasswordSecretId,
        enablePasswordSecretId: cliDetails.enablePasswordSecretId,
        sshPrivateKeySecretId: cliDetails.sshPrivateKeySecretId,
        sshPassphraseSecretId: cliDetails.sshPassphraseSecretId
      };
      if (cliDetails.cyberarkStoreId) {
        (details as any).cyberarkStoreId = cliDetails.cyberarkStoreId;
      }
      return details as CredentialUpdateDetails;
    }
    if (kind === CredentialKindschema.Gnmi && gnmiDetails) {
      return {
        username: gnmiDetails.username,
        port: gnmiDetails.port,
        passwordSecretId: gnmiDetails.passwordSecretId,
        deviceServerTrustCaBundleSecretId: gnmiDetails.deviceServerTrustCaBundleSecretId
      } as CredentialUpdateDetails;
    }
    if (kind === CredentialKindschema.SnmpV2c && snmpV2cDetails) {
      return {
        port: snmpV2cDetails.port,
        readCommunitySecretId: snmpV2cDetails.readCommunitySecretId,
        writeCommunitySecretId: snmpV2cDetails.writeCommunitySecretId
      };
    }
    if (kind === CredentialKindschema.SnmpV3 && snmpV3Details) {
      return {
        username: snmpV3Details.username,
        port: snmpV3Details.port,
        engineId: snmpV3Details.engineId,
        contextName: snmpV3Details.contextName,
        securityLevel: snmpV3Details.securityLevel,
        authProtocol: snmpV3Details.authProtocol,
        authPasswordSecretId: snmpV3Details.authPasswordSecretId,
        privProtocol: snmpV3Details.privProtocol,
        privPasswordSecretId: snmpV3Details.privPasswordSecretId
      };
    }
    if (kind === CredentialKindschema.ApiRest && apiDetails) {
      return {
        auth: apiDetails.auth
      };
    }
    return undefined;
  };

  const buildUpdateDetailsForRecord = (record: Credential): CredentialUpdateDetails | undefined => {
    if (record.kind === CredentialKindschema.CliSsh) {
      const details = record.details as CliDetailsWithCyberark;
      const payload: CliUpdateDetailsWithAuth = {
        username: details.username,
        port: details.port,
        execPasswordSecretId: details.execPasswordSecretId,
        enablePasswordSecretId: details.enablePasswordSecretId,
        sshPrivateKeySecretId: details.sshPrivateKeySecretId,
        sshPassphraseSecretId: details.sshPassphraseSecretId
      };
      if (details.cyberarkStoreId) {
        (payload as any).cyberarkStoreId = details.cyberarkStoreId;
      }
      return payload as CredentialUpdateDetails;
    }
    if (record.kind === CredentialKindschema.Gnmi) {
      const details = record.details as CredentialDetailsGnmischema;
      return {
        username: details.username,
        port: details.port,
        passwordSecretId: details.passwordSecretId,
        deviceServerTrustCaBundleSecretId: details.deviceServerTrustCaBundleSecretId
      } as CredentialUpdateDetails;
    }
    if (record.kind === CredentialKindschema.SnmpV2c) {
      const details = record.details as CredentialDetailsSnmpV2cschema;
      return {
        port: details.port,
        readCommunitySecretId: details.readCommunitySecretId,
        writeCommunitySecretId: details.writeCommunitySecretId
      };
    }
    if (record.kind === CredentialKindschema.SnmpV3) {
      const details = record.details as CredentialDetailsSnmpV3schema;
      return {
        username: details.username,
        port: details.port,
        engineId: details.engineId,
        contextName: details.contextName,
        securityLevel: details.securityLevel,
        authProtocol: details.authProtocol,
        authPasswordSecretId: details.authPasswordSecretId,
        privProtocol: details.privProtocol,
        privPasswordSecretId: details.privPasswordSecretId
      };
    }
    if (record.kind === CredentialKindschema.ApiRest) {
      const details = record.details as CredentialDetailsApiRestschema;
      return {
        auth: details.auth
      };
    }
    return undefined;
  };

  const renderSecretRow = (secretId: string, label: string) => {
    const secret = secretRecords[secretId]?.secret ?? null;
    const rotation = rotationState[secretId] ?? { value: '', error: null, saving: false, editing: false };
    return (
      <div key={secretId} className="secret-row">
        <div className="secret-row__info">
          <div>
            <p className="secret-row__label">{label}</p>
            {secretLoading ? (
              <p className="secret-row__meta">Loading fingerprint…</p>
            ) : (
              <p className="secret-row__meta">
                Fingerprint: {formatFingerprint(secret)} · Updated: {formatTimestamp(secret?.updatedAt)}
              </p>
            )}
          </div>
          <div className="button-row">
            {!rotation.editing ? (
              <button
                type="button"
                className="ui-button ui-button--ghost"
                onClick={() =>
                  setRotationState((prev) => ({
                    ...prev,
                    [secretId]: { value: '', error: null, saving: false, editing: true }
                  }))
                }
              >
                Rotate
              </button>
            ) : (
              <button
                type="button"
                className="ui-button ui-button--ghost"
                onClick={() =>
                  setRotationState((prev) => ({
                    ...prev,
                    [secretId]: { value: '', error: null, saving: false, editing: false }
                  }))
                }
              >
                Cancel
              </button>
            )}
          </div>
        </div>
        {rotation.editing && (
          <div className="secret-row__rotate">
            <label className="form-field">
              <span className="form-field__label">New value</span>
              <textarea
                className="textarea"
                value={rotation.value}
                onChange={(event) =>
                  setRotationState((prev) => ({
                    ...prev,
                    [secretId]: { ...rotation, value: event.target.value }
                  }))
                }
              />
            </label>
            <div className="button-row">
              <button
                type="button"
                className="ui-button ui-button--primary"
                onClick={() => handleRotate(secretId)}
                disabled={rotation.saving}
              >
                {rotation.saving ? 'Saving…' : 'Apply rotation'}
              </button>
            </div>
            {rotation.error && (
              <p className="org-shell__banner" role="alert">
                {rotation.error}
              </p>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderSecretsSection = () => {
    if (secretList.length === 0) {
      return null;
    }
    return (
      <section className="section-card">
        <h3 className="section-card__title">Secrets</h3>
        <p className="section-card__subtitle">Rotate sensitive material without recreating the credential.</p>
        <div className="secret-list">{secretList.map(([secretId, record]) => renderSecretRow(secretId, describeSecret(secretId, record.secret.type, credential?.kind)))}</div>
      </section>
    );
  };

  const describeSecret = (secretId: string, type: Secret['type'] | undefined, kind: Credential['kind'] | undefined): string => {
    if (secretLabelMap[secretId]) {
      return secretLabelMap[secretId];
    }
    if (!kind || !type) {
      return 'Secret';
    }
    if (kind === CredentialKindschema.CliSsh) {
      if (type === 'password') {
        return 'CLI password';
      }
      if (type === 'enable_password') {
        return 'Enable password';
      }
      if (type === 'ssh_private_key') {
        return 'SSH private key';
      }
      if (type === 'ssh_passphrase') {
        return 'SSH key passphrase';
      }
    }
    if (kind === CredentialKindschema.Gnmi) {
      if (type === 'password') {
        return 'gNMI password';
      }
      if (type === 'tls_ca_bundle_pem') {
        return 'Device CA bundle';
      }
    }
    if (kind === CredentialKindschema.SnmpV2c) {
      return type === 'snmp_community' ? 'Community string' : 'Secret';
    }
    if (kind === CredentialKindschema.SnmpV3) {
      if (type === 'snmp_auth_password') {
        return 'Auth password';
      }
      if (type === 'snmp_priv_password') {
        return 'Privacy password';
      }
    }
    if (kind === CredentialKindschema.ApiRest) {
      switch (type) {
        case 'api_key':
          return 'API key';
        case 'password':
          return 'Password';
        case 'bearer_token':
          return 'Bearer token';
        case 'tls_client_cert_pem':
          return 'Client certificate';
        case 'tls_client_key_pem':
          return 'Client key';
        case 'tls_ca_bundle_pem':
          return 'CA bundle';
        case 'certificate_pkcs12':
          return 'PKCS#12';
        default:
          return 'Secret';
      }
    }
    return 'Secret';
  };

  const renderDetailsSection = () => {
    if (!credential) {
      return null;
    }
    switch (credential.kind) {
      case CredentialKindschema.CliSsh:
        if (!cliDetails) {
          return null;
        }
        return (
          <section className="section-card">
            <h3 className="section-card__title">CLI configuration</h3>
            <div className="form-grid form-grid--two">
              <label className="form-field">
                <span className="form-field__label">Username</span>
                <input
                  className="input"
                  value={cliDetails.username}
                  onChange={(event) => setCliDetails((prev) => prev ? ({ ...prev, username: event.target.value }) : prev)}
                />
              </label>
              <label className="form-field">
                <span className="form-field__label">Port</span>
                <input
                  className="input"
                  type="number"
                  min={1}
                  max={65535}
                  value={cliDetails.port ?? 22}
                  onChange={(event) =>
                    setCliDetails((prev) => prev ? ({ ...prev, port: Number(event.target.value) || 22 }) : prev)
                  }
                />
              </label>
            </div>
            {cliDetails.cyberarkStoreId && (
              <label className="form-field">
                <span className="form-field__label">Credential store</span>
                <select
                  className="select"
                  value={cliDetails.cyberarkStoreId ?? ''}
                  onChange={(event) =>
                    setCliDetails((prev) => prev ? ({ ...prev, cyberarkStoreId: event.target.value }) : prev)
                  }
                >
                  <option value="">Select a CyberArk store</option>
                  {credentialStores.map((store) => (
                    <option key={store.id} value={store.id}>
                      {store.name}
                    </option>
                  ))}
                </select>
                {credentialStoresLoading && <span className="form-field__hint">Loading credential stores…</span>}
                {!credentialStoresLoading && credentialStores.length === 0 && (
                  <span className="form-field__hint">No credential stores available.</span>
                )}
                {credentialStoresError && (
                  <span className="form-field__hint">Failed to load credential stores: {credentialStoresError.message}</span>
                )}
              </label>
            )}
            <p className="helper-text">
              Primary authentication method was set during creation.
              {cliDetails.cyberarkStoreId
                ? ' CyberArk credentials are pulled at runtime.'
                : ' Rotate secrets below to change material.'}
            </p>
          </section>
        );
      case CredentialKindschema.Gnmi:
        if (!gnmiDetails) {
          return null;
        }
        return (
          <section className="section-card">
            <h3 className="section-card__title">gNMI configuration</h3>
            <div className="form-grid form-grid--two">
              <label className="form-field">
                <span className="form-field__label">Username</span>
                <input
                  className="input"
                  value={gnmiDetails.username}
                  onChange={(event) => setGnmiDetails((prev) => prev ? ({ ...prev, username: event.target.value }) : prev)}
                />
              </label>
              <label className="form-field">
                <span className="form-field__label">Port</span>
                <input
                  className="input"
                  type="number"
                  min={1}
                  max={65535}
                  value={gnmiDetails.port ?? 6030}
                  onChange={(event) =>
                    setGnmiDetails((prev) => prev ? ({ ...prev, port: Number(event.target.value) || 6030 }) : prev)
                  }
                />
              </label>
            </div>
            <p className="helper-text">
              The agent uses strict server-authenticated TLS. Rotate the gNMI password or Device CA bundle below to change secret material.
            </p>
          </section>
        );
      case CredentialKindschema.SnmpV2c:
        if (!snmpV2cDetails) {
          return null;
        }
        return (
          <section className="section-card">
            <h3 className="section-card__title">SNMP v2c configuration</h3>
            <label className="form-field">
              <span className="form-field__label">Port</span>
              <input
                className="input"
                type="number"
                min={1}
                max={65535}
                value={snmpV2cDetails.port ?? 161}
                onChange={(event) =>
                  setSnmpV2cDetails((prev) => prev ? ({ ...prev, port: Number(event.target.value) || 161 }) : prev)
                }
              />
            </label>
          </section>
        );
      case CredentialKindschema.SnmpV3:
        if (!snmpV3Details) {
          return null;
        }
        return (
          <section className="section-card">
            <h3 className="section-card__title">SNMP v3 configuration</h3>
            <div className="form-grid form-grid--two">
              <label className="form-field">
                <span className="form-field__label">Username</span>
                <input
                  className="input"
                  value={snmpV3Details.username}
                  onChange={(event) =>
                    setSnmpV3Details((prev) => prev ? ({ ...prev, username: event.target.value }) : prev)
                  }
                />
              </label>
              <label className="form-field">
                <span className="form-field__label">Port</span>
                <input
                  className="input"
                  type="number"
                  min={1}
                  max={65535}
                  value={snmpV3Details.port ?? 161}
                  onChange={(event) =>
                    setSnmpV3Details((prev) => prev ? ({ ...prev, port: Number(event.target.value) || 161 }) : prev)
                  }
                />
              </label>
            </div>
            <div className="form-grid form-grid--two">
              <label className="form-field">
                <span className="form-field__label">Engine ID</span>
                <input
                  className="input"
                  value={snmpV3Details.engineId ?? ''}
                  onChange={(event) =>
                    setSnmpV3Details((prev) => prev ? ({ ...prev, engineId: event.target.value || undefined }) : prev)
                  }
                />
              </label>
              <label className="form-field">
                <span className="form-field__label">Context name</span>
                <input
                  className="input"
                  value={snmpV3Details.contextName ?? ''}
                  onChange={(event) =>
                    setSnmpV3Details((prev) => prev ? ({ ...prev, contextName: event.target.value || undefined }) : prev)
                  }
                />
              </label>
            </div>
            <label className="form-field">
              <span className="form-field__label">Security level</span>
              <select
                className="select"
                value={snmpV3Details.securityLevel}
                onChange={(event) =>
                  setSnmpV3Details((prev) =>
                    prev ? ({ ...prev, securityLevel: event.target.value as SnmpV3SecurityLevelschema }) : prev
                  )
                }
              >
                <option value={SnmpV3SecurityLevelschema.NoAuthNoPriv}>noAuthNoPriv</option>
                <option value={SnmpV3SecurityLevelschema.AuthNoPriv}>authNoPriv</option>
                <option value={SnmpV3SecurityLevelschema.AuthPriv}>authPriv</option>
              </select>
            </label>
            {snmpV3Details.securityLevel !== SnmpV3SecurityLevelschema.NoAuthNoPriv && (
              <label className="form-field">
                <span className="form-field__label">Auth protocol</span>
                <select
                  className="select"
                  value={snmpV3Details.authProtocol ?? ''}
                  onChange={(event) =>
                    setSnmpV3Details((prev) =>
                      prev ? ({ ...prev, authProtocol: (event.target.value as SnmpV3AuthProtocolschema) || undefined }) : prev
                    )
                  }
                >
                  <option value="">Unset</option>
                  <option value={SnmpV3AuthProtocolschema.Md5}>MD5</option>
                  <option value={SnmpV3AuthProtocolschema.Sha}>SHA</option>
                  <option value={SnmpV3AuthProtocolschema.Sha224}>SHA-224</option>
                  <option value={SnmpV3AuthProtocolschema.Sha256}>SHA-256</option>
                  <option value={SnmpV3AuthProtocolschema.Sha384}>SHA-384</option>
                  <option value={SnmpV3AuthProtocolschema.Sha512}>SHA-512</option>
                </select>
              </label>
            )}
            {snmpV3Details.securityLevel === SnmpV3SecurityLevelschema.AuthPriv && (
              <label className="form-field">
                <span className="form-field__label">Privacy protocol</span>
                <select
                  className="select"
                  value={snmpV3Details.privProtocol ?? ''}
                  onChange={(event) =>
                    setSnmpV3Details((prev) =>
                      prev ? ({ ...prev, privProtocol: (event.target.value as SnmpV3PrivProtocolschema) || undefined }) : prev
                    )
                  }
                >
                  <option value="">Unset</option>
                  <option value={SnmpV3PrivProtocolschema.Des}>DES</option>
                  <option value={SnmpV3PrivProtocolschema.Aes128}>AES-128</option>
                  <option value={SnmpV3PrivProtocolschema.Aes192}>AES-192</option>
                  <option value={SnmpV3PrivProtocolschema.Aes256}>AES-256</option>
                </select>
              </label>
            )}
          </section>
        );
      case CredentialKindschema.ApiRest:
        if (!apiDetails) {
          return null;
        }
        const auth = apiDetails.auth;
        return (
          <section className="section-card">
            <h3 className="section-card__title">API authentication</h3>
            <p className="helper-text">Auth type was chosen during creation; rotate secrets to change material.</p>
            <div className="form-grid form-grid--two">
              <label className="form-field">
                <span className="form-field__label">Auth type</span>
                <input className="input" value={auth?.type ?? '—'} readOnly />
              </label>
              {auth?.type === 'api_key' && auth.apiKey && (
                <Fragment>
                  <label className="form-field">
                    <span className="form-field__label">Placement</span>
                    <input className="input" value={auth.apiKey._in ?? 'header'} readOnly />
                  </label>
                  <label className="form-field">
                    <span className="form-field__label">Parameter name</span>
                    <input
                      className="input"
                      value={auth.apiKey.name ?? ''}
                      onChange={(event) =>
                        setApiDetails((prev) =>
                          prev
                            ? ({
                                auth: {
                                  ...prev.auth,
                                  apiKey: { ...prev.auth?.apiKey, name: event.target.value }
                                } as CredentialDetailsApiRestAuth
                              })
                            : prev
                        )
                      }
                    />
                  </label>
                </Fragment>
              )}
              {auth?.type === 'basic' && auth.basic && (
                <label className="form-field">
                  <span className="form-field__label">Username</span>
                  <input
                    className="input"
                    value={auth.basic.username ?? ''}
                    onChange={(event) =>
                      setApiDetails((prev) =>
                        prev
                          ? ({
                              auth: {
                                ...prev.auth,
                                basic: { ...(prev.auth?.basic as CredentialDetailsApiRestAuthBasic), username: event.target.value }
                              }
                            })
                          : prev
                      )
                    }
                  />
                </label>
              )}
            </div>
          </section>
        );
      default:
        return null;
    }
  };

  const summarySection =
    credential && (
      <div className="record-summary">
        <div className="record-summary__grid">
          <div>
            <span>Kind</span>
            <strong>{credential.kind}</strong>
          </div>
          <div>
            <span>Secrets</span>
            <strong>{secretStatus}</strong>
          </div>
          <div>
            <span>Updated</span>
            <strong>{formatTimestamp(credential.updatedAt ?? credential.createdAt)}</strong>
          </div>
          <div>
            <span>Created</span>
            <strong>{formatTimestamp(credential.createdAt)}</strong>
          </div>
        </div>
        {onRequestAssignment && (
          <div className="record-summary__footer">
            <button type="button" className="ui-button ui-button--ghost" onClick={() => onRequestAssignment(credential)}>
              <KeyIcon size={16} /> <span>Create assignment</span>
            </button>
          </div>
        )}
      </div>
    );

  const drawerActions =
    credential && (
      <div className="drawer-icon-actions">
        <button
          type="button"
          className="drawer-icon-button"
          aria-label="Save credential"
          title="Save credential"
          onClick={handleSave}
          disabled={saving}
        >
          <SaveIcon size={18} />
        </button>
      </div>
    );

  const body = loading ? (
    <p>Loading credential…</p>
  ) : error ? (
    <p className="org-shell__banner" role="alert">
      {error}
    </p>
  ) : credential ? (
    <div>
      {summarySection}
      {saveError && <div className="form-error">{saveError}</div>}
      {labelsError && <div className="form-error">{labelsError}</div>}
      <div className="form-grid">
        <section className="section-card">
          <h3 className="section-card__title">Overview</h3>
          <div className="form-grid form-grid--two">
            <label className="form-field">
              <span className="form-field__label">Display name</span>
              <input className="input" value={name} onChange={(event) => setName(event.target.value)} />
            </label>
            <label className="form-field">
              <span className="form-field__label">Description</span>
              <input className="input" value={description} onChange={(event) => setDescription(event.target.value)} />
            </label>
          </div>
          <label className="form-field">
            <span className="form-field__label">Labels</span>
            <LabelPairsEditor pairs={labelPairs} onChange={setLabelPairs} />
          </label>
        </section>
        {renderDetailsSection()}
        {renderSecretsSection()}
      </div>
    </div>
  ) : (
    <p>No credential selected.</p>
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={credential ? credential.name ?? 'Credential' : 'Credential'}
      subtitle={credential ? `${credential.kind} credential` : ''}
      width="wide"
      actions={drawerActions}
    >
      {body}
    </Drawer>
  );
};

export default CredentialDrawer;
